import { ref, onMounted } from 'vue'
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth'
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { auth, googleProvider, db } from '@/services/firebase'
import { useReferral } from './useReferral'
import { usePointSettings } from './usePointSettings'

const user = ref(null)
const userProfile = ref(null)
const loading = ref(true)
const error = ref(null)

export function useAuth() {
  const { processSignupWithReferral, createUniqueReferralCode } = useReferral()
  const { settings, loadSettings } = usePointSettings()

  // 유저 프로필 가져오기 (기존 유저 slotTickets 필드 자동 추가)
  const fetchUserProfile = async (firebaseUser) => {
    if (!firebaseUser) {
      userProfile.value = null
      return null
    }

    const userRef = doc(db, 'users', firebaseUser.uid)
    const userSnap = await getDoc(userRef)

    if (userSnap.exists()) {
      const data = userSnap.data()
      
      // 기존 유저에게 slotTickets 필드가 없으면 추가
      if (data.slotTickets === undefined) {
        await updateDoc(userRef, {
          slotTickets: 0,
          lastAttendanceDate: null,
          updatedAt: serverTimestamp()
        })
        data.slotTickets = 0
        data.lastAttendanceDate = null
      }
      
      userProfile.value = { id: userSnap.id, ...data }
    } else {
      userProfile.value = null
    }

    return userProfile.value
  }

  // 신규 유저 프로필 생성
  const createNewUserProfile = async (firebaseUser, referralCode = null) => {
    const userRef = doc(db, 'users', firebaseUser.uid)
    
    const myReferralCode = await createUniqueReferralCode()

    const newProfile = {
      email: firebaseUser.email,
      displayName: firebaseUser.displayName || '',
      photoURL: firebaseUser.photoURL || '',
      phone: '',
      role: 'user',
      // 포인트 시스템
      points: 0,
      // 슬롯 티켓 시스템
      slotTickets: 0,
      lastAttendanceDate: null,
      // 추천인 시스템
      referralCode: myReferralCode,
      referredBy: null,
      referralCount: 0,
      // 프리미엄/뱃지
      isPremium: false,
      premiumUntil: null,
      badges: [],
      // 북마크
      bookmarks: [],
      // 타임스탬프
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    await setDoc(userRef, newProfile)
    
    const result = await processSignupWithReferral(firebaseUser.uid, referralCode)
    
    await fetchUserProfile(firebaseUser)

    return {
      profile: userProfile.value,
      signupBonus: result.signupBonus,
      referralBonus: result.referralBonus,
      myReferralCode: result.referralCode
    }
  }

  // 이메일 로그인
  const loginWithEmail = async (email, password) => {
    error.value = null
    try {
      const result = await signInWithEmailAndPassword(auth, email, password)
      user.value = result.user
      await fetchUserProfile(result.user)
      return result.user
    } catch (e) {
      error.value = getErrorMessage(e.code)
      throw e
    }
  }

  // 이메일 회원가입
  const registerWithEmail = async (email, password, referralCode = null) => {
    error.value = null
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password)
      user.value = result.user
      
      const signupResult = await createNewUserProfile(result.user, referralCode)
      
      return {
        user: result.user,
        ...signupResult
      }
    } catch (e) {
      error.value = getErrorMessage(e.code)
      throw e
    }
  }

  // 구글 로그인
  const loginWithGoogle = async (referralCode = null) => {
    error.value = null
    try {
      const result = await signInWithPopup(auth, googleProvider)
      user.value = result.user

      const userRef = doc(db, 'users', result.user.uid)
      const userSnap = await getDoc(userRef)

      if (userSnap.exists()) {
        await fetchUserProfile(result.user)
        return { user: result.user, isNewUser: false }
      } else {
        const signupResult = await createNewUserProfile(result.user, referralCode)
        return { 
          user: result.user, 
          isNewUser: true,
          ...signupResult 
        }
      }
    } catch (e) {
      error.value = getErrorMessage(e.code)
      throw e
    }
  }

  // 비밀번호 재설정
  const resetPassword = async (email) => {
    error.value = null
    try {
      await sendPasswordResetEmail(auth, email)
      return true
    } catch (e) {
      error.value = getErrorMessage(e.code)
      throw e
    }
  }

  // 로그아웃
  const logout = async () => {
    try {
      await signOut(auth)
      user.value = null
      userProfile.value = null
    } catch (e) {
      error.value = getErrorMessage(e.code)
      throw e
    }
  }

  // 프로필 새로고침
  const refreshProfile = async () => {
    if (user.value) {
      await fetchUserProfile(user.value)
    }
  }

  // Admin 체크
  const isAdmin = () => {
    return userProfile.value?.role === 'admin'
  }

  // 포인트 조회
  const getPoints = () => {
    return userProfile.value?.points || 0
  }

  // 티켓 조회
  const getTickets = () => {
    return userProfile.value?.slotTickets || 0
  }

  // 프리미엄 여부
  const isPremium = () => {
    if (!userProfile.value?.isPremium) return false
    
    if (userProfile.value.premiumUntil) {
      const now = new Date()
      const premiumUntil = userProfile.value.premiumUntil.toDate()
      return premiumUntil > now
    }
    
    return userProfile.value.isPremium
  }

  // 에러 메시지 한글화
  const getErrorMessage = (code) => {
    const messages = {
      'auth/email-already-in-use': '이미 사용 중인 이메일입니다.',
      'auth/invalid-email': '올바른 이메일 형식이 아닙니다.',
      'auth/invalid-credential': '이메일 또는 비밀번호가 올바르지 않습니다.',
      'auth/weak-password': '비밀번호는 6자 이상이어야 합니다.',
      'auth/user-not-found': '존재하지 않는 계정입니다.',
      'auth/wrong-password': '비밀번호가 올바르지 않습니다.',
      'auth/too-many-requests': '잠시 후 다시 시도해주세요.',
      'auth/popup-closed-by-user': '로그인이 취소되었습니다.',
      'auth/missing-email': '이메일을 입력해주세요.'
    }
    return messages[code] || '오류가 발생했습니다.'
  }

  // 인증 상태 감지
  onMounted(async () => {
    await loadSettings()

    onAuthStateChanged(auth, async (currentUser) => {
      user.value = currentUser
      if (currentUser) {
        await fetchUserProfile(currentUser)
      } else {
        userProfile.value = null
      }
      loading.value = false
    })
  })

  return {
    user,
    userProfile,
    loading,
    error,
    settings,
    loginWithEmail,
    registerWithEmail,
    loginWithGoogle,
    resetPassword,
    logout,
    refreshProfile,
    isAdmin,
    getPoints,
    getTickets,
    isPremium,
    fetchUserProfile
  }
}